import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  InputField,
  Select,
  SubmitButton,
} from '@saas-kit/ui';

import type { QmsCustomerType, QmsService } from './types';

export interface IssueTokenInput {
  service_id: string;
  customer_type_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
}

interface Props {
  services: QmsService[];
  customerTypes: QmsCustomerType[];
  pending: boolean;
  onSubmit: (input: IssueTokenInput) => void;
}

/** Walk-in token issuance form. State is local — on a successful issue the
 *  parent swaps this card for the receipt, so no explicit reset is needed. */
export function QmsIssueForm({
  services,
  customerTypes,
  pending,
  onSubmit,
}: Props): JSX.Element {
  const { t } = useTranslation('qms');
  const [serviceId, setServiceId] = useState('');
  const [customerTypeId, setCustomerTypeId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!serviceId) return;
    onSubmit({
      service_id: serviceId,
      customer_type_id: customerTypeId || null,
      customer_name: customerName.trim() || null,
      customer_phone: customerPhone.trim() || null,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('issue.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="max-w-md space-y-3">
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">{t('issue.service')}</span>
            <Select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              required
            >
              <option value="">—</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">
              {t('issue.customer_type')}
            </span>
            <Select
              value={customerTypeId}
              onChange={(e) => setCustomerTypeId(e.target.value)}
            >
              <option value="">{t('issue.customer_type_none')}</option>
              {customerTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Select>
          </label>
          <InputField
            label={t('issue.customer_name')}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
          <InputField
            label={t('issue.customer_phone')}
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
          />
          <SubmitButton
            loading={pending}
            label={t('issue.submit')}
            loadingLabel={t('issue.submit')}
          />
        </form>
      </CardContent>
    </Card>
  );
}
